import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import request from "../../Helpers/API";
import Swal from "sweetalert2";
import { LoaderScreen } from "../components/LoaderScreen";
import { MensajeriaFiltroBoton } from "./MensajeriaFiltroBoton";
import { MensajeriaIndicadoresTabla } from "./MensajeriaIndicadoresTabla";
import { SUCURSALES_CATALOGO } from "./mensajeriaRemasteredHelpers";

const formatoInputDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatearNumero = (n) => new Intl.NumberFormat("es-MX").format(n);

// Los porcentajes nunca deben rebasar 100 (ej. recibidos > enviados por reenvíos/históricos)
const topePct = (n) => Math.min(100, Math.max(0, n));

const HEADER_VACIO = {
    enviados: 0,
    entregados: 0,
    leidos: 0,
    interacciones: 0,
    interesados: 0,
    no_interesados: 0,
    respuesta_libre: 0,
    atendidos: 0,
    sin_atender: 0,
    con_error: 0,
    numero_erroneo: 0,
};

// Sucursales para el panel de filtros — mismo catálogo fijo que MensajeriaRemasteredScreen
const sucursalesFiltro = Object.entries(SUCURSALES_CATALOGO)
    .map(([id, nombre]) => ({ id: Number(id), nombre }))
    .sort((a, b) => (a.id % 10) - (b.id % 10) || a.id - b.id);

// Indicadores de mensajería — ContactosMensajeriaMenu/ObtenerIndicadore (header, masRespuestas,
// menosRespuestas, listado). Los top 5 y la tabla de detalle no navegan a otra pantalla: la
// tabla solo expande/colapsa clientes por responsable (MensajeriaIndicadoresTabla).
export const MensajeriaIndicadoresScreen = () => {
    const history = useHistory();
    const year = new Date().getFullYear();

    const [filtros, setFiltros] = useState({
        fechaInicio: formatoInputDate(new Date(year, 0, 1)),
        fechaFin: formatoInputDate(new Date()),
        sucursales: [],
        adr: [],
        linea: "Consolidado",
        plantilla: "",
    });

    const [header, setHeader] = useState(HEADER_VACIO);
    const [masRespuestas, setMasRespuestas] = useState([]);
    const [menosRespuestas, setMenosRespuestas] = useState([]);
    const [listado, setListado] = useState([]);
    const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

    const cargarIndicadores = () => {
        // Sin selección se envía el default de cada parámetro (nunca se omite ni va null):
        // linea="T" (Todas), plantilla="CC" (Consolidado), adr="0", sucursal="0"
        const linea = filtros.linea && filtros.linea !== "Consolidado" ? filtros.linea : "T";
        const sucursal = filtros.sucursales.length > 0 ? filtros.sucursales.join(",") : "0";
        const adr = filtros.adr.length > 0 ? filtros.adr.join(",") : "0";
        const plantilla = filtros.plantilla || "CC";

        const params = new URLSearchParams({
            fechainicio: filtros.fechaInicio,
            fechafin: filtros.fechaFin,
            linea,
            adr,
            sucursal,
            plantilla,
        });

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get(`ContactosMensajeriaMenu/ObtenerIndicadore?${params.toString()}`)
            .then((result) => {
                setHeader(result?.header ?? HEADER_VACIO);
                setMasRespuestas(Array.isArray(result?.masRespuestas) ? result.masRespuestas : []);
                setMenosRespuestas(Array.isArray(result?.menosRespuestas) ? result.menosRespuestas : []);
                setListado(Array.isArray(result?.listado) ? result.listado : []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch((error) => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("INDICADORES DE MENSAJERIA", JSON.stringify(error), "error");
            });
    };

    useEffect(() => {
        cargarIndicadores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        enviados,
        entregados,
        leidos,
        interacciones,
        interesados,
        no_interesados: noInteresados,
        respuesta_libre: respuestaLibre,
        atendidos,
        sin_atender: sinAtender,
        con_error: conError,
        numero_erroneo: numeroErroneo,
    } = header;
    const total = enviados;
    const pctEntregados = enviados > 0 ? topePct(Math.round((entregados / enviados) * 1000) / 10) : 0;
    const pctLeidos = entregados > 0 ? topePct(Math.round((leidos / entregados) * 1000) / 10) : 0;
    const pctInteresados = interacciones > 0 ? topePct(Math.round((interesados / interacciones) * 100)) : 0;
    const pctNoInteresados = interacciones > 0 ? topePct(Math.round((noInteresados / interacciones) * 100)) : 0;
    const pctRespuestaLibre = interacciones > 0 ? topePct(Math.round((respuestaLibre / interacciones) * 100)) : 0;
    const pctAtendidos = interacciones > 0 ? topePct(Math.round((atendidos / interacciones) * 100)) : 0;
    const pctSinAtender = interacciones > 0 ? topePct(Math.round((sinAtender / interacciones) * 100)) : 0;
    const pctConError = enviados > 0 ? topePct(Math.round((conError / enviados) * 100)) : 0;
    const pctNumeroErroneo = enviados > 0 ? topePct(Math.round((numeroErroneo / enviados) * 100)) : 0;

    return (
        <div className="crm-main-screen">
            <LoaderScreen loader={loader} />
            <div className="crm-body">
                <div className="crm-page">
                    <div className="crm-module">
                        <div className="crm-module-head">
                            <div className="crm-head-grid">
                                <div className="crm-head-left">
                                    <button className="crm-btn-regresar" onClick={() => history.push("/Mensajeria/Main")}>
                                        <ArrowBackIcon /> Regresar
                                    </button>
                                    <MensajeriaFiltroBoton
                                        filtros={filtros}
                                        setFiltros={setFiltros}
                                        sucursales={sucursalesFiltro}
                                        onAplicar={cargarIndicadores}
                                    />
                                </div>
                                <div className="crm-head-center">
                                    <h1 className="crm-module-title">INDICADORES DE MENSAJERÍA</h1>
                                </div>
                                <div className="crm-head-right" />
                            </div>
                            <div className="crm-head-divider">
                                <div className="crm-head-divider-dot" />
                            </div>
                        </div>

                        <div className="mjr-wrapper scroll">
                            <div className="mjr-body">
                                <div className="mjr-ind-contenido scroll">
                                    <div className="mjr-columna mjr-ind-general">
                                        <div className="mjr-columna-header">
                                            <div className="mjr-columna-header-icono"><ChatBubbleOutlineIcon /></div>
                                            <h3 className="mjr-columna-titulo">Indicadores General</h3>
                                            <span className="mjr-columna-badge">
                                                {formatearNumero(total)} total
                                                {sinAtender > 0 && (
                                                    <span className="mjr-columna-badge-alerta">{formatearNumero(sinAtender)} sin atender</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mjr-ind-kpis">
                                        <div className="mjr-ind-kpi mjr-ind-kpi-neutro">
                                            <SendIcon />
                                            <span className="mjr-ind-kpi-numero">{formatearNumero(enviados)}</span>
                                            <span className="mjr-ind-kpi-label">Enviados</span>
                                        </div>
                                        <div className="mjr-ind-kpi mjr-ind-kpi-neutro">
                                            <DoneIcon />
                                            <span className="mjr-ind-kpi-numero">{formatearNumero(entregados)}</span>
                                            <span className="mjr-ind-kpi-label">Entregados ({pctEntregados}%)</span>
                                        </div>
                                        <div className="mjr-ind-kpi mjr-ind-kpi-verde">
                                            <DoneAllIcon />
                                            <span className="mjr-ind-kpi-numero">{formatearNumero(leidos)}</span>
                                            <span className="mjr-ind-kpi-label">Leídos ({pctLeidos}%)</span>
                                        </div>
                                        <div className="mjr-ind-kpi mjr-ind-kpi-verde-suave mjr-ind-kpi-interacciones">
                                            <div className="mjr-ind-kpi-interacciones-principal">
                                                <StarBorderIcon />
                                                <span className="mjr-ind-kpi-numero">{formatearNumero(interacciones)}</span>
                                                <span className="mjr-ind-kpi-label">Interacciones</span>
                                            </div>
                                            <div className="mjr-ind-kpi-leyenda">
                                                <span className="mjr-ind-kpi-leyenda-item">
                                                    <i className="mjr-ind-kpi-minibar-a" />
                                                    Interesados <b>{formatearNumero(interesados)}</b> ({pctInteresados}%)
                                                </span>
                                                <span className="mjr-ind-kpi-leyenda-item">
                                                    <i className="mjr-ind-kpi-minibar-b" />
                                                    No interesados <b>{formatearNumero(noInteresados)}</b> ({pctNoInteresados}%)
                                                </span>
                                                <span className="mjr-ind-kpi-leyenda-item">
                                                    <i className="mjr-ind-kpi-minibar-c" />
                                                    Respuesta libre <b>{formatearNumero(respuestaLibre)}</b> ({pctRespuestaLibre}%)
                                                </span>
                                            </div>
                                            <div className="mjr-ind-kpi-leyenda">
                                                <span className="mjr-ind-kpi-leyenda-item">
                                                    <i className="mjr-ind-kpi-minibar-ok" />
                                                    Atendidos <b>{formatearNumero(atendidos)}</b> ({pctAtendidos}%)
                                                </span>
                                                <span className="mjr-ind-kpi-leyenda-item">
                                                    <i className="mjr-ind-kpi-minibar-alerta" />
                                                    Sin atender <b>{formatearNumero(sinAtender)}</b> ({pctSinAtender}%)
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mjr-ind-kpi mjr-ind-kpi-ambar">
                                            <ErrorOutlineIcon />
                                            <span className="mjr-ind-kpi-numero">{formatearNumero(conError)}</span>
                                            <span className="mjr-ind-kpi-label">Con error ({pctConError}%)</span>
                                        </div>
                                        <div className="mjr-ind-kpi mjr-ind-kpi-gris">
                                            <PhonelinkEraseIcon />
                                            <span className="mjr-ind-kpi-numero">{formatearNumero(numeroErroneo)}</span>
                                            <span className="mjr-ind-kpi-label">Número erróneo ({pctNumeroErroneo}%)</span>
                                        </div>
                                    </div>

                                    <div className="mjr-ind-panel-fila">
                                        <div className="mjr-ind-panel mjr-ind-panel-top">
                                            <h4 className="mjr-ind-panel-titulo">Top 5 que más responden</h4>
                                            <div className="mjr-ind-barras">
                                                {masRespuestas.slice(0, 5).map((r) => (
                                                    <div key={r.idresponsable} className="mjr-ind-barra-row">
                                                        <span className="mjr-ind-barra-nombre" title={r.responsable}>{r.responsable}</span>
                                                        <div className="mjr-ind-barra-track">
                                                            <div className="mjr-ind-barra-fill mjr-ind-barra-fill-verde" style={{ width: `${topePct(r.pct_respuesta)}%` }} />
                                                        </div>
                                                        <span className="mjr-ind-barra-pct">{topePct(r.pct_respuesta)}%</span>
                                                    </div>
                                                ))}
                                                {masRespuestas.length === 0 && (
                                                    <p className="mjr-lista-vacio">Sin datos para el periodo/filtros seleccionados.</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mjr-ind-panel mjr-ind-panel-top">
                                            <h4 className="mjr-ind-panel-titulo">Top 5 que menos responden</h4>
                                            <div className="mjr-ind-barras">
                                                {menosRespuestas.slice(0, 5).map((r) => (
                                                    <div key={r.idresponsable} className="mjr-ind-barra-row">
                                                        <span className="mjr-ind-barra-nombre" title={r.responsable}>{r.responsable}</span>
                                                        <div className="mjr-ind-barra-track">
                                                            <div className="mjr-ind-barra-fill mjr-ind-barra-fill-rojo" style={{ width: `${topePct(r.pct_respuesta)}%` }} />
                                                        </div>
                                                        <span className="mjr-ind-barra-pct">{topePct(r.pct_respuesta)}%</span>
                                                    </div>
                                                ))}
                                                {menosRespuestas.length === 0 && (
                                                    <p className="mjr-lista-vacio">Sin datos para el periodo/filtros seleccionados.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mjr-ind-panel mjr-ind-panel-tabla">
                                        <h4 className="mjr-ind-panel-titulo">Detalle por responsable</h4>
                                        <MensajeriaIndicadoresTabla listado={listado} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
